// Custom field filters across a whole applied selection - the `{<filterType>: values, <filterType>_operators}`
// object the shared filter panel stores per section. A filter the list cannot honour (its field is no
// longer available on the list, its dropdown options were deleted, or it is malformed) is dropped together
// with its Exclude toggle before anything is matched, so an ignored excluded filter can never hide every row.
import moment, {type Moment} from 'moment';
import type {CustomFieldColumnDef} from '../custom-field-columns/customFieldColumnModel';
import {FILTER_OPERATOR, getEntityOperatorKey, isOperator} from '../filters/filter_logic';
import {FILTER_SECTIONS} from '../filters/filter_sections';
import {EMPTY_OPTION, customFieldFilterHit, isValidCondition, type ValueCondition} from './customFieldFilterConditions';
import {definitionIdOf, isCustomFieldFilterKey} from './customFieldFilterKeys';

export type FilterSelection = Record<string, unknown>;

const OPERATORS_SUFFIX = '_operators';
const baseKeyOf = (key: string): string => (isOperator(key) ? key.slice(0, -OPERATORS_SUFFIX.length) : key);
const definitionFor = (defs: CustomFieldColumnDef[], key: string): CustomFieldColumnDef | undefined =>
	defs.find(def => def.id === definitionIdOf(key));

const isValidSelectionValue = (def: CustomFieldColumnDef, value: unknown): boolean =>
	def.kind === 'DROPDOWN'
		? value === EMPTY_OPTION || def.options.some(option => option.id === value)
		: isValidCondition(def, value);

/**
 * A field's values as the filter holds them: known dropdown options once each; one number or date
 * condition; text terms that share one condition, or is empty on its own. Anything else is dropped.
 */
export function normaliseValues(def: CustomFieldColumnDef, values: readonly unknown[]): unknown[] {
	const valid = values.filter(value => isValidSelectionValue(def, value));
	if (def.kind === 'DROPDOWN') return Array.from(new Set(valid));
	if (valid.length === 0) return [];
	if (def.kind !== 'TEXT') return [valid[0]];
	const {op} = valid[0] as ValueCondition;
	return op === 'empty' ? [valid[0]] : valid.filter(value => (value as ValueCondition).op === op);
}

export function sanitizeCustomFieldSelection(
	section: FilterSelection | null | undefined,
	defs: CustomFieldColumnDef[]
): FilterSelection {
	const clean: FilterSelection = {};
	Object.entries(section ?? {}).forEach(([key, value]) => {
		if (!isCustomFieldFilterKey(baseKeyOf(key))) {
			clean[key] = value;
			return;
		}
		// A custom field's Exclude is copied below, only when its filter survives.
		if (isOperator(key)) return;
		const def = definitionFor(defs, key);
		if (!def || !Array.isArray(value)) return;
		const kept = normaliseValues(def, value);
		if (kept.length === 0) return;
		clean[key] = kept;
		const operators = section?.[getEntityOperatorKey(key)];
		if (operators) clean[getEntityOperatorKey(key)] = operators;
	});
	return clean;
}

/** Every custom field filter in the selection, ANDed; Exclude negates one field's verdict. */
export function buildCustomFieldPredicate<N>(
	section: FilterSelection | null | undefined,
	defs: CustomFieldColumnDef[],
	valuesOf: (node: N) => Record<string, string | null>,
	today: Moment = moment()
): (node: N) => boolean {
	const clean = sanitizeCustomFieldSelection(section, defs);
	const checks = Object.keys(clean)
		.filter(isCustomFieldFilterKey)
		.map(key => ({
			def: definitionFor(defs, key) as CustomFieldColumnDef,
			selection: clean[key] as unknown[],
			excluded:
				(clean[getEntityOperatorKey(key)] as Record<string, boolean> | undefined)?.[FILTER_OPERATOR.EXCLUDE] === true,
		}));
	if (checks.length === 0) return () => true;
	return node => {
		const values = valuesOf(node);
		return checks.every(({def, selection, excluded}) => {
			const hit = customFieldFilterHit(def, values[def.key], selection, today);
			return excluded ? !hit : hit;
		});
	};
}

/** One row of the panel's applied-filter list. `id` is an option id or, for text, number and date, a condition. */
export interface AppliedFilterEntry {
	section: string;
	filterType: string;
	id: unknown;
}

/** What the host list can honour right now. */
export interface FilterAvailability {
	customFieldDefinitions: CustomFieldColumnDef[];
	unavailableFilterTypes: string[];
}

const isFilterTypeAvailable = (filterType: string, availability: FilterAvailability): boolean =>
	!availability.unavailableFilterTypes.includes(filterType) &&
	(!isCustomFieldFilterKey(filterType) || definitionFor(availability.customFieldDefinitions, filterType) !== undefined);

export function isAppliedFilterAvailable(entry: AppliedFilterEntry, availability: FilterAvailability): boolean {
	if (!isFilterTypeAvailable(entry.filterType, availability)) return false;
	if (!isCustomFieldFilterKey(entry.filterType)) return true;
	const def = definitionFor(availability.customFieldDefinitions, entry.filterType) as CustomFieldColumnDef;
	return isValidSelectionValue(def, entry.id);
}

/** A field's whole next selection, holding a term typed in but not yet added. Closing applies it. */
export interface CustomFieldDraft {
	filterType: string;
	values: unknown[];
}

export function withCustomFieldDrafts(entries: AppliedFilterEntry[], drafts: CustomFieldDraft[]): AppliedFilterEntry[] {
	if (drafts.length === 0) return entries;
	const drafted = new Set(drafts.map(draft => draft.filterType));
	return entries
		.filter(entry => !(entry.section === FILTER_SECTIONS.PROJECTS && drafted.has(entry.filterType)))
		.concat(
			drafts.flatMap(draft =>
				draft.values.map(id => ({section: FILTER_SECTIONS.PROJECTS, filterType: draft.filterType, id}))
			)
		);
}

/** The Filter button's number: one per native filter value, and one per custom field however many values it holds. */
export function filterButtonCount(entries: readonly AppliedFilterEntry[]): number {
	const customFields = new Set(
		entries
			.filter(entry => isCustomFieldFilterKey(entry.filterType))
			.map(entry => `${entry.section}\u0000${entry.filterType}`)
	);
	return entries.filter(entry => !isCustomFieldFilterKey(entry.filterType)).length + customFields.size;
}
